"use client"; // Next.js 13 ve üzeri için istemci tarafı bileşeni olduğunu belirtir

import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
// Swiper bileşenlerini ve modüllerini içe aktarıyoruz
import {
  Autoplay,
  Navigation,
  Pagination,
  Virtual,
  Zoom,
} from "swiper/modules";
import { Swiper as CustomSwiper } from "swiper/react";
// Swiper stillerini içe aktarıyoruz

import "swiper/css";
import "swiper/css/virtual";
//import "swiper/css/navigation";
//import "swiper/css/pagination";
//import "swiper/css/autoplay";
//import "swiper/css/zoom";

// Default değerler
const DEFAULT_SWIPER_CONFIG = {
  pagination: {
    bulletActiveClass: "bg-primary w-8",
    bulletClass:
      "h-3 rounded-full transition-colors duration-300 w-3 bg-gray-300",
    containerClass: "mt-10 justify-center gap-2 flex h-3",
  },
  navigation: {
    prevButtonClass:
      "absolute top-[40%] -left-5 z-10 m-0 hidden  h-12 w-12 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-white p-[10px] shadow lg:flex",
    nextButtonClass:
      "absolute top-[40%] -right-5 z-10 m-0 hidden  h-12 w-12 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-white p-[10px] shadow lg:flex",
    containerClass: "swiper-controller",
  },
};
// Ref ile dışarıdan kontrol edilebilecek handle fonksiyonları
const Swiper = forwardRef(
  ({ customClass = "", swiperOptions = {}, children, onSlideChange }, ref) => {
    const swiperRef = useRef(null);
    const previousVideoRef = useRef(null);
    const prevRef = useRef(null);
    const nextRef = useRef(null);

    // Swiper pagination modülünü kullanacaksa ekliyoruz
    const enhancedSwiperOptions = useMemo(() => {
      const modules = [];
      let pagination = {};
      let navigation = {};
      let autoplay = {};

      if (swiperOptions.zoom) {
        modules.push(Zoom);
      }
      const virtualOpts = swiperOptions.virtual;
      const isVirtualActive =
        virtualOpts === true ||
        (typeof virtualOpts === "object" &&
          virtualOpts !== null &&
          virtualOpts.enabled !== false);
      if (isVirtualActive) {
        modules.push(Virtual);
      }
      if (swiperOptions.pagination) {
        modules.push(Pagination);

        const defaultBulletClass = DEFAULT_SWIPER_CONFIG.pagination.bulletClass;
        const defaultBulletActiveClass =
          DEFAULT_SWIPER_CONFIG.pagination.bulletActiveClass;

        const bulletClass =
          swiperOptions.pagination?.bulletClass || defaultBulletClass;
        const bulletActiveClass =
          swiperOptions.pagination?.bulletActiveClass ||
          defaultBulletActiveClass;

        pagination = {
          el: swiperOptions?.pagination?.el || ".swiper-pagination",
          clickable: true,
          bulletActiveClass: bulletActiveClass || defaultBulletActiveClass,
          bulletClass: bulletClass || defaultBulletClass,
          containerClass:
            swiperOptions.pagination?.containerClass ||
            DEFAULT_SWIPER_CONFIG.pagination.containerClass,
          ...(swiperOptions.pagination?.type && {
            type: swiperOptions.pagination.type,
          }),
          ...(swiperOptions.pagination?.dynamicBullets !== undefined && {
            dynamicBullets: swiperOptions.pagination.dynamicBullets,
          }),
          ...(swiperOptions.pagination?.dynamicMainBullets !== undefined && {
            dynamicMainBullets: swiperOptions.pagination.dynamicMainBullets,
          }),
          ...(swiperOptions.pagination?.hideOnClick !== undefined && {
            hideOnClick: swiperOptions.pagination.hideOnClick,
          }),
        };
      }
      if (swiperOptions.navigation) {
        modules.push(Navigation);
        navigation = {
          nextEl: nextRef.current,
          prevEl: prevRef.current,
        };
      }

      if (swiperOptions.autoplay) {
        modules.push(Autoplay);
        autoplay = swiperOptions.autoplay;
      } else if (swiperOptions.duration) {
        modules.push(Autoplay);
        autoplay = {
          delay: swiperOptions.duration,
          disableOnInteraction: false,
        };
      }

      // Swiper'ın desteklediği prop'ları filtrele
      const {
        paginationCustomClass,
        navigationCustomClass,
        navigationPrevButtonCustomClass,
        navigationNextButtonCustomClass,
        // Prevent leaking custom props to DOM/CustomSwiper
        navigationPrevButtonClass,
        navigationNextButtonClass,
        navigationPrevButtonIcon,
        navigationNextButtonIcon,
        paginationContainerClass,
        navigationContainerClass,
        ...swiperSupportedOptions
      } = swiperOptions;

      return {
        ...swiperSupportedOptions,
        ...(swiperOptions.pagination && { pagination }),
        ...(swiperOptions.navigation && { navigation }),
        ...(swiperOptions.autoplay && { autoplay }),
        ...(swiperOptions.duration && { autoplay }),
        modules,
      };
    }, [swiperOptions]);

    const handleVideoPlay = useCallback(() => {
      const swiper = swiperRef.current?.swiper;
      if (swiper?.autoplay) {
        swiper.autoplay.stop();
      }
    }, []);

    const handleVideoEnded = useCallback(() => {
      const swiper = swiperRef.current?.swiper;
      if (swiper) {
        if (swiper.loop) {
          swiper.slideNext();
        } else {
          if (swiper.isEnd) {
            swiper.slideTo(0, 300); // İlk slide'a dön (0 ms geçiş süresiyle)
          } else {
            swiper.slideNext();
          }
        }

        if (swiper.autoplay) swiper.autoplay.start();
      }
    }, []);

    const handleVideoTimeUpdate = useCallback((event) => {
      const target = event.currentTarget;
      if (target) {
        // no-op: keeps listener attachment consistent with video lifecycle.
      }
    }, []);

    const handleSlideChange = useCallback(() => {
      const swiper = swiperRef.current?.swiper;
      if (!swiper) return;

      const activeSlide = swiper.slides[swiper.activeIndex];
      const videoElement = activeSlide?.querySelector("video");

      // Önceki slide'daki video elementinden olay dinleyicilerini kaldır
      if (previousVideoRef.current) {
        previousVideoRef.current.removeEventListener("play", handleVideoPlay);
        previousVideoRef.current.removeEventListener("ended", handleVideoEnded);
        previousVideoRef.current.removeEventListener(
          "timeupdate",
          handleVideoTimeUpdate,
        );
      }

      // Yeni slide'daki video elementine olay dinleyicilerini ekle
      if (videoElement) {
        videoElement.addEventListener("play", handleVideoPlay);
        videoElement.addEventListener("ended", handleVideoEnded);
        videoElement.addEventListener("timeupdate", handleVideoTimeUpdate);
        previousVideoRef.current = videoElement; // Mevcut video elementini previousVideoRef'e ata
      } else {
        previousVideoRef.current = null;
      }
    }, [handleVideoPlay, handleVideoEnded, handleVideoTimeUpdate]);

    // Navigation ref'lerini swiper'a bağla
    useEffect(() => {
      const swiper = swiperRef.current?.swiper;
      if (!swiper || !swiperOptions.navigation) return;

      // Navigation ref'lerini güncelle
      if (prevRef.current && nextRef.current) {
        // eslint-disable-next-line react-hooks/immutability
        swiper.params.navigation.prevEl = prevRef.current;

        swiper.params.navigation.nextEl = nextRef.current;
        swiper.navigation.init();
        swiper.navigation.update();
      }
    }, [swiperOptions.navigation]);

    // addToListener; handleSlideChange ve video event
    useEffect(() => {
      const swiper = swiperRef.current?.swiper;
      if (!swiper) return;

      handleSlideChange();
      swiper.on("slideChange", handleSlideChange);

      return () => {
        swiper.off("slideChange", handleSlideChange);
        if (previousVideoRef.current) {
          previousVideoRef.current.removeEventListener("play", handleVideoPlay);
          previousVideoRef.current.removeEventListener(
            "ended",
            handleVideoEnded,
          );
          previousVideoRef.current.removeEventListener(
            "timeupdate",
            handleVideoTimeUpdate,
          );
        }
      };
    }, [
      handleSlideChange,
      handleVideoPlay,
      handleVideoEnded,
      handleVideoTimeUpdate,
    ]);

    // Dışarıya expose edilecek fonksiyonlar
    useImperativeHandle(ref, () => ({
      slideTo: (index, speed = 300) => {
        const swiper = swiperRef.current?.swiper;
        if (swiper) {
          swiper.slideTo(index, speed);
        }
      },
      slideToLoop: (index, speed = 300) => {
        const swiper = swiperRef.current?.swiper;
        if (!swiper) return;
        if (typeof swiper.slideToLoop === "function") {
          swiper.slideToLoop(index, speed);
          return;
        }
        swiper.slideTo(index, speed);
      },
      nextSlide: () => {
        const swiper = swiperRef.current?.swiper;
        if (swiper) {
          swiper.slideNext();
        }
      },
      prevSlide: () => {
        const swiper = swiperRef.current?.swiper;
        if (swiper) {
          swiper.slidePrev();
        }
      },
    }));

    return (
      <div
        className={clsx(
          "swiper-container relative overflow-visible",
          customClass,
        )}
      >
        <CustomSwiper
          ref={swiperRef}
          {...enhancedSwiperOptions} // Swiper seçeneklerini yayıyoruz
          className={clsx("swiper h-full w-full")} // CSS sınıflarını birleştiriyoruz
          onSlideChange={(swiper) => {
            if (onSlideChange) {
              onSlideChange(swiper); // onSwiper'i dışarıya ilet
            }
          }}
        >
          {children}

          {/* pagination */}
          {swiperOptions.pagination && !swiperOptions.pagination?.el && (
            <div
              className={clsx(
                "swiper-pagination",
                swiperOptions.pagination?.containerClass ||
                  swiperOptions.paginationContainerClass ||
                  DEFAULT_SWIPER_CONFIG.pagination.containerClass,
                swiperOptions.paginationCustomClass,
              )}
            ></div>
          )}
        </CustomSwiper>

        {/* navigation */}
        {swiperOptions.navigation && (
          <div
            className={clsx(
              "swiper-navigations",
              swiperOptions.navigationContainerClass ||
                DEFAULT_SWIPER_CONFIG.navigation.containerClass,
            )}
          >
            {swiperOptions.navigation && (
              <>
                <div
                  ref={prevRef}
                  role="button"
                  tabIndex={0}
                  className={clsx(
                    "swiper-button-prev",
                    swiperOptions.navigationPrevButtonClass ||
                      DEFAULT_SWIPER_CONFIG.navigation.prevButtonClass,
                    swiperOptions.navigationPrevButtonCustomClass,
                    swiperOptions.navigationCustomClass,
                  )}
                  onClick={(e) => e.stopPropagation()}
                >
                  {swiperOptions.navigationPrevButtonIcon ? (
                    swiperOptions.navigationPrevButtonIcon
                  ) : (
                    <FontAwesomeIcon
                      className="h-6 w-6"
                      icon={byPrefixAndName.far["chevron-left"]}
                    />
                  )}
                </div>
                <div
                  ref={nextRef}
                  role="button"
                  tabIndex={0}
                  className={clsx(
                    "swiper-button-next",
                    swiperOptions.navigationNextButtonClass ||
                      DEFAULT_SWIPER_CONFIG.navigation.nextButtonClass,
                    swiperOptions.navigationNextButtonCustomClass,
                    swiperOptions.navigationCustomClass,
                  )}
                  onClick={(e) => e.stopPropagation()}
                >
                  {swiperOptions.navigationNextButtonIcon ? (
                    swiperOptions.navigationNextButtonIcon
                  ) : (
                    <FontAwesomeIcon
                      className="h-6 w-6"
                      icon={byPrefixAndName.far["chevron-right"]}
                    />
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    );
  },
);

Swiper.displayName = "Swiper"; // Referans ile kullanılan componentin displayName'ini belirtiyoruz

// Swiper bileşenini dışa aktarıyoruz
export default Swiper;
